import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiButton,
  MiDetailPageHeader,
  MiIcon,
  MiLink,
  MiList,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  ENTITY_NAMES,
  ICONS_WEB,
  MODAL_TITLES,
  PAGINATION,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { mdiChevronLeft, mdiPlus } from "@mdi/js";
import * as mdi from "@mdi/js";
import { Box, useTheme } from "@mui/material";
import { AddNewIssueTemplateModal } from "../components/AddNewIssueTemplateModal";
import { useIssueTemplateViewModel } from "../useIssueTemplateViewModel";

export function IssueTemplateList() {
  const router = useRouter();
  const { classes } = useStyles();
  const theme = useTheme();
  const {
    issueTemplates,
    stateManager,
    getIssueTemplates,
    openAddModal,
    setOpenAddModal,
    openDeleteModal,
    setOpenDeleteModal,
    selectedItem,
    setSelectedItem,
    onCreate,
    onDelete,
    onDeleteCancel,
    assignmentTemplates,
    trades,
    getTrades,
    getAssignmentTemplates,
  } = useIssueTemplateViewModel();

  useEffect(() => {
    getIssueTemplates();
    getAssignmentTemplates();
    getTrades();
  }, []);

  const templateFieldset = [
    {
      field: "name",
      headerName: "Name",
      headerAlign: "left",
      align: "left",
      groupable: false,
      flex: 1,
      renderCell: (p) => (
        <MiLink
          key={p.row.id}
          title={p.value}
          to={`/settings/order-management/issue-types/${p.row.id}/details`}
        />
      ),
    },
    {
      field: "recordType",
      headerName: "Record Type",
      groupable: false,
      headerAlign: "left",
      align: "left",
      flex: 1,
    },
    {
      field: "defaultCausedByTradeName",
      headerName: "Caused By Trade",
      headerAlign: "left",
      align: "left",
      groupable: false,
      flex: 1,
    },
    {
      field: "defaultResolvedByTradeName",
      headerName: "Resolved By Trade",
      headerAlign: "left",
      align: "left",
      groupable: false,
      flex: 1,
    },
  ];

  const getRowActions = (row) => [
    {
      text: BUTTON_LABELS.DELETE,
      onClick: () => {
        setSelectedItem(row);
        setOpenDeleteModal(true);
      },
      renderIcon: () => (
        <MiIcon
          path={mdi[ICONS_WEB.TRASH_CAN]}
          size={1}
          color={theme.palette.red.main}
        />
      ),
    },
  ];

  const customOnBack = () => {
    router.navigate("/settings/order-management");
  };
  return (
    <Box className={classes.root}>
      <MiDetailPageHeader
        title={ENTITY_NAMES.ISSUE_TEMPLATES}
        titleIcon={mdiChevronLeft}
        customOnBack={customOnBack}
        loading={stateManager.isBusy()}
        actionComponent={
          <MiButton
            title={`Add ${ENTITY_NAMES.ISSUE_TEMPLATE}`}
            variant={BUTTON_VARIANTS.CONTAINED}
            startIcon={{
              path: mdiPlus,
              iconSize: 0.8,
            }}
            data-testid="add-issue-template-btn"
            onClick={() => setOpenAddModal(true)}
          />
        }
      />
      <MiBox>
        <MiList
          columns={templateFieldset}
          data={issueTemplates}
          paginationMode={PAGINATION.CLIENT}
          loading={stateManager.isBusy()}
          getRowActions={getRowActions}
          disableRowSelectionOnClick
          data-testid="issue-templates-table"
        />
      </MiBox>
      {openAddModal && (
        <AddNewIssueTemplateModal
          open={openAddModal}
          onModalClose={() => setOpenAddModal(false)}
          onSubmit={onCreate}
          loading={stateManager.isBusy()}
          assignmentTemplates={assignmentTemplates}
          trades={trades}
        />
      )}
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        description={`Are you sure you want to delete the ${ENTITY_NAMES.ISSUE_TEMPLATE}: ${selectedItem?.name}?`}
        open={openDeleteModal}
        handlePositive={onDelete}
        handleNegative={onDeleteCancel}
        className={classes.modal}
        textPositive={BUTTON_LABELS.DELETE}
      />
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "inherit",
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  container: {
    height: "inherit",
    overflowY: "scroll",
    margin: 5,
    borderRadius: 10,
    padding: 5,
    backgroundColor: theme.palette.common.white,
  },
  modal: {
    minWidth: 400,
  },
}));
